import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import debounce from "lodash/debounce";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import InputAdornment from "@material-ui/core/InputAdornment";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import A_SimpleDialog from "../../../Atoms/Feedback/Dialogs/A_SimpleDialog.jsx";
import A_Button from "../../../Atoms/Inputs/Buttons/A_Button";
import A_TextField from "../../../Atoms/Inputs/TextFields/A_TextField";
import M_DataStateRenderer from "../../../Molecules/Common/DataStateRenderer/M_DataStateRenderer.jsx";
import LoaderSkeleton from "../../../Molecules/Common/LoaderSkeleton";
import TablePaginationFooter from "../DrawdownApproval/TablePaginationFooter";
import IconAction from "./IconAction";
import { COLUMNS, DocumentTableRow } from "./FirmDocumentsTable";
import { api, buildFirmDocumentsQueryParams } from "./ProfessionalContacts.helper";
import useStyles from "./ProfessionalContacts.styles";

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

/**
 * The full document list behind the Documents section's "View all {count} documents" link
 * (FIRM-DOCUMENTS-020, LLD §0.4). 2026-09-22 revision, part 3 (FIRM-DOCUMENTS-033, LLD §0.11):
 * server-side paginated/sorted/searched against GET .../documents, matching
 * AssociatedLoansDialog.jsx's shape on this same screen — the section's own `documents` array is
 * now only a 5-row preview (FIRM-DOCUMENTS-032), so this dialog fetches its own page rather than
 * filtering/sorting whatever the section happened to have already loaded. Still shares
 * COLUMNS/DocumentTableRow with FirmDocumentsTable.jsx for the row markup; `sortDocuments` (the
 * client-side comparator) is no longer needed here — FirmDocumentsTable.jsx still uses it for its
 * own, still-client-side-sorted 5-row preview.
 *
 * Layout (LLD §0.11): A_SimpleDialog's own DialogContent scrolls as one block by default, which is
 * why the search bar used to scroll away with the table. The fix lives entirely inside the div this
 * component passes as dialogContent: a bounded-height flex column (dialogScrollShell) with three
 * children — the fixed search block, a table wrapper that is the ONLY scrolling region
 * (dialogScrollBody), and a fixed TablePaginationFooter — so DialogContent itself never needs to
 * scroll. `stickyTableHead` pins the header cells to the top of that one scrolling region.
 */
const FirmDocumentsAllDialog = ({
  open,
  onClose,
  firmId,
  refreshToken,
  onDownload,
  onDelete,
  busyId,
}) => {
  const classes = useStyles();
  const [searchText, setSearchText] = useState("");
  const [sort, setSort] = useState("uploadedUtc");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resetToken, setResetToken] = useState(0);
  const requestSeq = useRef(0);
  const didMount = useRef(false);

  const load = useCallback(
    async (params) => {
      const seq = ++requestSeq.current;
      setLoading(true);
      setError(null);
      try {
        const result = await api.listFirmDocuments(
          firmId,
          buildFirmDocumentsQueryParams(params)
        );
        if (seq !== requestSeq.current) return;
        setItems((result && result.items) || []);
        setTotal((result && result.total) || 0);
      } catch (err) {
        if (seq === requestSeq.current) setError(err);
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    },
    [firmId]
  );

  const debouncedLoad = useMemo(() => debounce(load, 350), [load]);

  useEffect(() => () => debouncedLoad.cancel(), [debouncedLoad]);

  // Mount-once initial load (this dialog is only mounted while `open`, mirroring
  // AssociatedLoansDialog.jsx — see FirmDocumentsSection.jsx's conditional render).
  useEffect(() => {
    load({ page: 1, pageSize: rowsPerPage, sort, sortDir });
  }, []);

  // A delete fired from inside this modal is confirmed and executed by FirmDocumentsSection.jsx
  // (the shared ConfirmDialog/handleConfirmDelete this dialog has never owned — only `onDelete`,
  // the click that opens that confirmation, is wired here). FirmDocumentsSection.jsx bumps its own
  // `refreshToken` after that delete succeeds, for its 5-row preview's refetch; this dialog is
  // handed the same token and reloads its OWN current page whenever it changes, so the two
  // independently-fetched views of the same server data stay in sync without either one guessing
  // at the other's state. Skips the very first render — the mount effect above already covers it.
  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    load({
      q: searchText,
      sort,
      sortDir,
      page: page + 1,
      pageSize: rowsPerPage,
    });
  }, [refreshToken]);

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchText(value);
    setPage(0);
    debouncedLoad({ q: value, sort, sortDir, page: 1, pageSize: rowsPerPage });
  };

  const handleSort = (key) => {
    const nextDir = sort === key ? (sortDir === "asc" ? "desc" : "asc") : "asc";
    setSort(key);
    setSortDir(nextDir);
    setPage(0);
    load({
      q: searchText,
      sort: key,
      sortDir: nextDir,
      page: 1,
      pageSize: rowsPerPage,
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    load({
      q: searchText,
      sort,
      sortDir,
      page: newPage + 1,
      pageSize: rowsPerPage,
    });
  };

  const handleChangeRowsPerPage = (event) => {
    const newSize = parseInt(event.target.value, 10);
    setRowsPerPage(newSize);
    setPage(0);
    load({ q: searchText, sort, sortDir, page: 1, pageSize: newSize });
  };

  const clearSearch = () => {
    setSearchText("");
    setPage(0);
    setResetToken((token) => token + 1);
    load({ sort, sortDir, page: 1, pageSize: rowsPerPage });
  };

  return (
    <A_SimpleDialog
      open={open}
      onClose={onClose}
      title={<span className={classes.dialogTitleText}>All documents ({total})</span>}
      maxWidth="md"
      fullWidth
      dialogContent={
        <div className={clsx(classes.dialogPaper, classes.dialogScrollShell)}>
          <div className={clsx(classes.toolbar, classes.dialogScrollFixed)}>
            <div className={classes.searchBlock}>
              <A_TextField
                key={resetToken}
                id="firm-documents-search"
                className={classes.searchInput}
                label="Search"
                defaultValue={searchText}
                onChange={handleSearchChange}
                margin="none"
                variant="outlined"
                size="small"
                // MUI v4's outlined variant doesn't shift the label to clear a startAdornment in
                // its resting (unfocused, empty) state, so without this the "Search" label
                // renders behind/overlapping the search icon, looking cut off. Forcing it
                // permanently shrunk (a fixed caption above the box, never inline) sidesteps the
                // bug entirely instead of only masking it while a value is present.
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        style={{
                          fontSize: "1rem",
                          color: "var(--color-text-secondary)",
                        }}
                      />
                    </InputAdornment>
                  ),
                  // A_TextField already wraps whatever's passed here in its own outer
                  // <InputAdornment position="end">, so this is the bare content, not a second one.
                  endAdornment: searchText ? (
                    <IconAction
                      title="Clear search"
                      icon={<ClearIcon style={{ fontSize: "1rem" }} />}
                      onClick={clearSearch}
                    />
                  ) : null,
                }}
              />
            </div>
          </div>

          <div
            className={clsx(
              classes.tableContainer,
              classes.tableSpaced,
              classes.dialogScrollBody
            )}
          >
            <M_DataStateRenderer
              loading={loading && items.length === 0}
              error={error}
              data={items}
              isEmpty={!loading && !error && items.length === 0}
              empty={
                <div className={classes.emptyState}>
                  {searchText
                    ? "No documents match your search."
                    : "No documents."}
                </div>
              }
              skeleton={<LoaderSkeleton />}
            >
              <Table size="small" className={classes.table}>
                <TableHead className={clsx(classes.tableHead, classes.stickyTableHead)}>
                  <TableRow>
                    {COLUMNS.map((col) => (
                      <TableCell
                        key={col.key}
                        sortDirection={sort === col.key ? sortDir : false}
                      >
                        <TableSortLabel
                          className={classes.sortLabel}
                          active={sort === col.key}
                          direction={sort === col.key ? sortDir : "asc"}
                          onClick={() => handleSort(col.key)}
                        >
                          {col.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody className={classes.tableBody}>
                  {items.map((document) => (
                    <DocumentTableRow
                      key={document.documentId}
                      document={document}
                      busyId={busyId}
                      onDownload={onDownload}
                      onDelete={onDelete}
                    />
                  ))}
                </TableBody>
              </Table>
            </M_DataStateRenderer>
          </div>

          <div className={classes.dialogScrollFixed}>
            <TablePaginationFooter
              count={total}
              page={page}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
            />
          </div>
        </div>
      }
      dialogActions={
        <A_Button label="Close" premiumVariant="secondary" onClick={onClose} />
      }
    />
  );
};

FirmDocumentsAllDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  firmId: PropTypes.string.isRequired,
  refreshToken: PropTypes.number,
  onDownload: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  busyId: PropTypes.string,
};

export default FirmDocumentsAllDialog;
